import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, XCircle, Clock, CreditCard, ArrowRight, X } from 'lucide-react';
import { Button } from '../ui/button';

interface SubscriptionAlertBannerProps {
  status: 'past_due' | 'canceled' | 'canceling';
  planName?: string;
  periodEndDate?: string;
  onDismiss?: () => void;
  dismissible?: boolean;
}

/**
 * Persistent alert banner for subscription issues
 * Shows at the top of the page for past_due, canceled, or cancel_at_period_end statuses
 */
export const SubscriptionAlertBanner: React.FC<SubscriptionAlertBannerProps> = ({
  status,
  planName,
  periodEndDate,
  onDismiss,
  dismissible = false
}) => {
  const getConfig = () => {
    switch (status) {
      case 'past_due':
        return {
          icon: <AlertTriangle className="h-5 w-5" />,
          bgClass: 'bg-red-600 dark:bg-red-700',
          textClass: 'text-white',
          title: 'Payment Failed',
          message: 'Your subscription payment failed. Please update your payment method to avoid service interruption.',
          actionLabel: 'Update Payment',
          actionLink: '/billing?tab=payment',
          secondaryLabel: 'View Details',
          secondaryLink: '/billing',
          urgent: true
        };
      case 'canceled':
        return {
          icon: <XCircle className="h-5 w-5" />,
          bgClass: 'bg-gray-700 dark:bg-gray-800',
          textClass: 'text-white',
          title: 'Subscription Canceled',
          message: 'Your subscription has been canceled. Resubscribe to continue using all features.',
          actionLabel: 'View Plans',
          actionLink: '/billing?tab=plans',
          secondaryLabel: 'Contact Support',
          secondaryLink: '/support',
          urgent: false
        };
      case 'canceling':
        return {
          icon: <Clock className="h-5 w-5" />,
          bgClass: 'bg-orange-500 dark:bg-orange-600',
          textClass: 'text-white',
          title: 'Subscription Ending Soon',
          message: periodEndDate
            ? `Your ${planName || 'subscription'} will end on ${new Date(periodEndDate).toLocaleDateString()}. Reactivate to keep your access.`
            : `Your ${planName || 'subscription'} is scheduled to cancel at the end of the billing period.`,
          actionLabel: 'Reactivate Subscription',
          actionLink: '/billing',
          secondaryLabel: undefined,
          secondaryLink: undefined,
          urgent: false
        };
      default:
        return null;
    }
  };

  const config = getConfig();
  if (!config) return null;

  return (
    <div className={`${config.bgClass} ${config.textClass} fixed top-0 left-0 right-0 z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3 gap-4">
          {/* Left side - Icon and message */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`flex-shrink-0 ${config.urgent ? 'animate-pulse' : ''}`}>
              {config.icon}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm sm:text-base">
                {config.title}
              </p>
              <p className="text-xs sm:text-sm opacity-90 truncate sm:whitespace-normal">
                {config.message}
              </p>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {config.secondaryLink && config.secondaryLabel && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden sm:flex text-white hover:bg-white/20 border-white/30"
              >
                <Link to={config.secondaryLink}>
                  {config.secondaryLabel}
                </Link>
              </Button>
            )}

            <Button
              asChild
              size="sm"
              className="bg-white text-gray-900 hover:bg-gray-100 font-medium gap-1"
            >
              <Link to={config.actionLink}>
                <CreditCard className="h-4 w-4 hidden sm:block" />
                {config.actionLabel}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>

            {dismissible && onDismiss && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="text-white hover:bg-white/20 p-1 h-auto"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
